import React, { createContext, useContext, useState, ReactNode } from 'react';

export type NetworkType = 'ethereum' | 'polygon' | 'arbitrum' | 'optimism' | 'base' | 'bsc' | 'avalanche' | 'linea';

export interface NetworkConfig {
  id: NetworkType;
  name: string;
  chainId?: number;
  icon: string;
  color: string;
}

export const NETWORKS: Record<NetworkType, NetworkConfig> = {
  ethereum: {
    id: 'ethereum',
    name: 'Ethereum',
    chainId: 1,
    icon: 'https://tokens.1inch.io/0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee.png',
    color: '#627EEA',
  },
  polygon: {
    id: 'polygon',
    name: 'Polygon',
    chainId: 137,
    icon: 'https://tokens.1inch.io/0x7d1afa7b718fb893db30a3abc0cfc608aacfebb0.png',
    color: '#8247E5',
  },
  arbitrum: {
    id: 'arbitrum',
    name: 'Arbitrum',
    chainId: 42161,
    icon: '/arblogowebp.webp',
    color: '#28A0F0',
  },
  optimism: {
    id: 'optimism',
    name: 'Optimism',
    chainId: 10,
    icon: 'https://tokens.1inch.io/0x4200000000000000000000000000000000000042.png',
    color: '#FF0420',
  },
  base: {
    id: 'base',
    name: 'Base',
    chainId: 8453,
    icon: '/baselogowebpl.webp',
    color: '#0052FF',
  },
  bsc: {
    id: 'bsc',
    name: 'BSC',
    chainId: 56,
    icon: '/bsclogowebpl.webp',
    color: '#F0B90B',
  },
  avalanche: {
    id: 'avalanche',
    name: 'Avalanche',
    chainId: 43114,
    icon: 'https://tokens.1inch.io/0xb31f66aa3c1e785363f0875a1b74e27b85fd66c7.png',
    color: '#E84142',
  },
  linea: {
    id: 'linea',
    name: 'Linea',
    chainId: 59144,
    icon: 'https://s2.coinmarketcap.com/static/img/coins/64x64/27657.png',
    color: '#d3d3d3',
  },
};

interface NetworkContextType {
  selectedNetwork: NetworkType;
  setSelectedNetwork: (network: NetworkType) => void;
  networkConfig: NetworkConfig;
}

const NetworkContext = createContext<NetworkContextType | undefined>(undefined);

export const NetworkProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedNetwork, setSelectedNetwork] = useState<NetworkType>('polygon');

  const networkConfig = NETWORKS[selectedNetwork];

  return (
    <NetworkContext.Provider
      value={{
        selectedNetwork,
        setSelectedNetwork,
        networkConfig,
      }}
    >
      {children}
    </NetworkContext.Provider>
  );
};

export const useNetwork = (): NetworkContextType => {
  const context = useContext(NetworkContext);
  if (!context) {
    throw new Error('useNetwork must be used within a NetworkProvider');
  }
  return context;
};

